import {
  NavBar,
  NavBarContent,
  NavBarFooter,
  NavBarItem,
  PRODUCTS,
} from '@cint/wonderment';
import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { useAuth } from '@/auth';
import { ROUTES } from '@/constants';
import { useNavigationContext } from '@/context/NavigationContext';
import { useRouteParams } from '@/hooks/useRouteParams';

import { NavigationItems } from './NavigationItems';
import { useNavigationPermissions } from './useNavigationPermissions';

const PrimaryNavRoutes = [
  { label: 'Panels', href: ROUTES.panels },
  { label: 'Panels', href: ROUTES.createPanel },
  { label: 'Panels', href: ROUTES.companies },
  { label: 'Panels', href: ROUTES.exportPanel },
  { label: 'Redemption methods', href: ROUTES.paymentMethods },
  { label: 'Redemption methods', href: ROUTES.createPaymentMethod },
  { label: 'Voucher code management', href: ROUTES.voucherCode },
  { label: 'Bulk banning', href: ROUTES.bulkBanning },
  { label: 'Bulk balance adjustment', href: ROUTES.bulkBalanceAdjustment },
  { label: 'Email domain blocklist', href: ROUTES.emailBlocklist },
  { label: 'Buyer ID blocklist', href: ROUTES.buyerIdBlocklist },
  { label: 'Panelist support', href: ROUTES.panelists },
  { label: 'Tracking partner templates', href: ROUTES.signalTemplates },
  { label: 'Translations', href: ROUTES.translations },
  { label: 'Translations', href: ROUTES.createTranslation },
  { label: 'Recruitment insights', href: ROUTES.recruitmentInsights },
  { label: 'Panel insights', href: ROUTES.overviewInsights },
  { label: 'Billing & Invoicing', href: ROUTES.billingInsights },
  { label: 'Targeted activation', href: ROUTES.targetedSampling },
  { label: 'Profiling', href: ROUTES.profiling },
] as const;

const PanelNavSegments: { segment: string; label: string }[] = [
  { segment: '/settings', label: 'Settings' },
  { segment: '/branding', label: 'Branding' },
  { segment: '/url', label: 'URL' },
  { segment: '/panelRedemptionMethods', label: 'Panel redemption methods' },
  { segment: '/dynamicIncentiveSettings', label: 'Incentives & margins' },
  { segment: '/incentiveSettings', label: 'Incentives & margins' },
  { segment: '/panelistImport', label: 'Panelist import' },
  { segment: '/leadImport', label: 'Lead import' },
  {
    segment: '/manualActivationManagement',
    label: 'Manual activation management',
  },
  { segment: '/marketingCampaigns', label: 'Marketing campaigns' },
  { segment: '/createMarketingCampaign', label: 'Marketing campaigns' },
  { segment: '/referralProgram', label: 'Referral program' },
];

const resolveActiveLabel = (pathname: string): string | null => {
  const panelMatch = PanelNavSegments.find(({ segment }) =>
    pathname.includes(segment)
  );
  if (panelMatch) return panelMatch.label;

  const topMatch = PrimaryNavRoutes.find(({ href }) =>
    pathname.startsWith(href)
  );
  return topMatch?.label ?? null;
};

export const Navigation = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { id: panelId, companyId } = useRouteParams();

  const [selectedItem, setSelectedItem] = useState<string | null>(null);
  const [expandedSection, setExpandedSection] = useState<
    'panelManagement' | 'rewards' | 'trustAndSafety' | 'analytics' | null
  >(null);

  const permissions = useNavigationPermissions(panelId);
  const { setPanelContext } = useNavigationContext();

  useEffect(() => {
    if (panelId) {
      setPanelContext(panelId);
    }
  }, [panelId, setPanelContext]);

  const handleNavigation = useCallback(
    (href: string, label: string) => {
      setSelectedItem(label);
      setExpandedSection(null);
      navigate(href);
    },
    [navigate]
  );

  const {
    panelManagementItems,
    rewardsItems,
    trustAndSafetyItems,
    analyticsItems,
    panelManagementLabels,
    rewardsLabels,
    trustAndSafetyLabels,
    analyticsLabels,
  } = NavigationItems({
    selectedItem,
    handleNavigation,
    panelId,
    companyId,
    permissions,
  });

  useEffect(() => {
    const activeLabel = resolveActiveLabel(location.pathname);
    setSelectedItem(activeLabel);

    const sectionByLabels: [typeof expandedSection, any[]][] = [
      ['panelManagement', panelManagementLabels],
      ['rewards', rewardsLabels],
      ['trustAndSafety', trustAndSafetyLabels],
      ['analytics', analyticsLabels],
    ];

    const matched = sectionByLabels.find(
      ([, labels]) => activeLabel && labels.includes(activeLabel)
    );
    setExpandedSection(matched?.[0] ?? null);
  }, [
    location.pathname,
    panelManagementLabels,
    rewardsLabels,
    trustAndSafetyLabels,
    analyticsLabels,
  ]);

  const {
    hasPermissionToViewPanel,
    hasPermissionToViewPanelist,
    hasPermissionToViewSignalTemplates,
    hasPermissionToViewTranslations,
    hasPermissionToViewReports,
    hasPermissionToViewPaymentMethods,
    hasPermissionToViewPaymentMethodCodes,
    hasPermissionToViewBulkBalanceAdjust,
    hasPermissionToViewBulkBan,
    hasPermissionToViewEmailBlocklist,
    isAppCuesOn,
    hasPermissionToViewTargetedSampling,
    hasPermissionToViewProfiling,
  } = permissions;

  return (
    <NavBar
      headerText={{ firstLine: 'Cint', secondLine: 'Engage' }}
      product={PRODUCTS.ENGAGE}
      userMenu={{
        avatar: {
          alt: user?.name ?? user?.email,
          src: user?.picture ?? undefined,
        },
        hasUserDescription: true,
        label: user?.name ?? 'User',
        userEmail: user?.email ?? '',
        terms: {
          label: 'Help',
          onClick: () => {
            window.open(
              'https://engagesupport.cint.com/hc/en-us',
              '_blank',
              'noopener,noreferrer'
            );
          },
        },
        signOut: {
          label: 'Sign out',
          onClick: async () => {
            await logout();
          },
        },
      }}
    >
      <NavBarContent>
        {hasPermissionToViewPanel && (
          <NavBarItem
            iconName="SquaresFour"
            label="Panels"
            isActiveItem={selectedItem === 'Panels'}
            onClick={() => handleNavigation(ROUTES.panels, 'Panels')}
          />
        )}

        {hasPermissionToViewPanel && (
          <NavBarItem
            key={`panel-mgmt-${expandedSection === 'panelManagement'}`}
            iconName="ClipboardText"
            label="Panel management"
            isActiveItem={panelManagementLabels.includes(selectedItem as any)}
            hasDefaultOpen={expandedSection === 'panelManagement'}
            onClick={() => setExpandedSection('panelManagement')}
            secondaryItems={panelManagementItems}
          />
        )}

        {(hasPermissionToViewPaymentMethods ||
          hasPermissionToViewPaymentMethodCodes) &&
          rewardsItems.length > 0 && (
            <NavBarItem
              key={`rewards-${expandedSection === 'rewards'}`}
              iconName="SealCheck"
              label="Rewards"
              isActiveItem={rewardsLabels.includes(selectedItem as any)}
              hasDefaultOpen={expandedSection === 'rewards'}
              onClick={() => setExpandedSection('rewards')}
              secondaryItems={rewardsItems}
            />
          )}

        {(hasPermissionToViewBulkBalanceAdjust ||
          hasPermissionToViewBulkBan ||
          hasPermissionToViewEmailBlocklist) &&
          trustAndSafetyItems.length > 0 && (
            <NavBarItem
              key={`trust-safety-${expandedSection === 'trustAndSafety'}`}
              iconName="ShieldCheck"
              label="Trust & safety"
              isActiveItem={trustAndSafetyLabels.includes(selectedItem as any)}
              hasDefaultOpen={expandedSection === 'trustAndSafety'}
              onClick={() => setExpandedSection('trustAndSafety')}
              secondaryItems={trustAndSafetyItems}
            />
          )}

        {hasPermissionToViewPanelist && (
          <NavBarItem
            iconName="Headset"
            label="Panelist support"
            isActiveItem={selectedItem === 'Panelist support'}
            onClick={() =>
              handleNavigation(ROUTES.panelists, 'Panelist support')
            }
          />
        )}

        {hasPermissionToViewProfiling && (
          <NavBarItem
            iconName="IdentificationCard"
            label="Profiling"
            isActiveItem={selectedItem === 'Profiling'}
            onClick={() => handleNavigation(ROUTES.profiling, 'Profiling')}
          />
        )}

        {hasPermissionToViewSignalTemplates && (
          <NavBarItem
            iconName="Path"
            label="Tracking partner templates"
            isActiveItem={selectedItem === 'Tracking partner templates'}
            onClick={() =>
              handleNavigation(
                ROUTES.signalTemplates,
                'Tracking partner templates'
              )
            }
          />
        )}

        {hasPermissionToViewTranslations && (
          <NavBarItem
            iconName="Translate"
            label="Translations"
            isActiveItem={selectedItem === 'Translations'}
            onClick={() =>
              handleNavigation(ROUTES.translations, 'Translations')
            }
          />
        )}

        {hasPermissionToViewReports && (
          <NavBarItem
            key={`analytics-${expandedSection === 'analytics'}`}
            iconName="ChartBar"
            label="Analytics"
            isActiveItem={analyticsLabels.includes(selectedItem as any)}
            hasDefaultOpen={expandedSection === 'analytics'}
            onClick={() => setExpandedSection('analytics')}
            secondaryItems={analyticsItems}
          />
        )}

        {hasPermissionToViewTargetedSampling && (
          <NavBarItem
            iconName="Crosshair"
            label="Targeted activation"
            isActiveItem={selectedItem === 'Targeted activation'}
            onClick={() =>
              handleNavigation(ROUTES.targetedSampling, 'Targeted activation')
            }
          />
        )}
      </NavBarContent>
      <NavBarFooter>
        {isAppCuesOn && (
          <div className="nav-question-mark">
            <NavBarItem iconName="Question" label="Help" />
          </div>
        )}
      </NavBarFooter>
    </NavBar>
  );
};
